/**
 * Mock messages — threaded conversations on every active service request,
 * mixing inbound and outbound so the inbox UI looks lively.
 */

import type { Tables } from '@/lib/db.types'
import {
  messageEntityId, requestEntityId, customerUserId, providerUserId, DEV_USER_IDS,
} from './ids'

type Message = Tables<'messages'>

const NOW = Date.parse('2026-05-01T08:00:00.000Z')
const T = (mins: number) => new Date(NOW + mins * 60_000).toISOString()

interface MessageSeed {
  requestN: number
  senderId: string
  body: string
  minutesAgo: number
  read: boolean
}

const SEEDS: MessageSeed[] = [
  // Request 2 — dev customer's bohagsflytt (quoted)
  { requestN: 2, senderId: providerUserId(1),  body: 'Hej Sigge! Bekräftar att vi är försäkrade för upp till 200 000 kr om något skulle hända.', minutesAgo: 60 * 24 * 4, read: true },
  { requestN: 2, senderId: DEV_USER_IDS.customer, body: 'Tack! En fråga — har ni egna flyttkartonger?', minutesAgo: 60 * 24 * 3, read: true },
  { requestN: 2, senderId: providerUserId(1),  body: 'Ja, vi har upp till 50 kartonger gratis (inkluderat i priset). Sedan 25 kr/st.', minutesAgo: 60 * 24 * 3 - 30, read: true },
  { requestN: 2, senderId: providerUserId(11), body: 'Hej! Vi har också möjlighet — kan komma redan på fredag om det passar bättre.', minutesAgo: 60 * 24 * 2, read: false },

  // Request 3 — elektriker (booked)
  { requestN: 3, senderId: providerUserId(3),  body: 'Hej Sigge! Jag kan ta jobbet på onsdag kl 14:00. Tar med mig allt material.', minutesAgo: 60 * 24 * 11, read: true },
  { requestN: 3, senderId: DEV_USER_IDS.customer, body: 'Perfekt, det går bra. Portkod är 1234.', minutesAgo: 60 * 24 * 11 - 60, read: true },
  { requestN: 3, senderId: providerUserId(3),  body: 'Tack — räknar med ca 2 timmar för fyra armaturer.', minutesAgo: 60 * 24 * 10, read: true },

  // Request 6 — VVS open (no provider has quoted yet, no dev customer involvement)
  { requestN: 6, senderId: customerUserId(1),  body: 'Hej, hur snabbt kan ni komma? Det läcker mer och mer.', minutesAgo: 60 * 12, read: false },

  // Request 8 — trädfällning (quoted)
  { requestN: 8, senderId: providerUserId(7),  body: 'Hej! Vi kan komma onsdag eller torsdag nästa vecka. Vilket passar bäst?', minutesAgo: 60 * 24 * 5, read: true },
  { requestN: 8, senderId: customerUserId(3),  body: 'Torsdag funkar fint!', minutesAgo: 60 * 24 * 5 - 90, read: true },

  // Request 9 — TV-bänk (dev provider quoted)
  { requestN: 9, senderId: DEV_USER_IDS.provider, body: 'Hej Filip! Tack för förfrågan. Jag har skickat en offert — passar det att titta på ritningar?', minutesAgo: 60 * 24 * 1.5, read: true },
  { requestN: 9, senderId: customerUserId(4),  body: 'Hej Sigge! Ja gärna, kan vi ta ett kort möte ikväll?', minutesAgo: 60 * 24, read: false },
  { requestN: 9, senderId: DEV_USER_IDS.provider, body: 'Visst, 19:00 funkar bra. Ringer dig då.', minutesAgo: 60 * 22, read: false },

  // Request 12 — säsongsavtal gräsklippning (dev provider quoted via mismatched data but still rendered)
  { requestN: 12, senderId: providerUserId(7), body: 'Hej Tilde! Vi tar gärna säsongen. Första klippet på måndag?', minutesAgo: 60 * 48, read: true },
  { requestN: 12, senderId: customerUserId(7), body: 'Måndag fungerar.', minutesAgo: 60 * 44, read: true },

  // Request 15 — pianoflytt
  { requestN: 15, senderId: providerUserId(20), body: 'Hej! Vi har ledigt 28 maj kl 10. Hör av dig om det fungerar.', minutesAgo: 60 * 6, read: false },
]

export const MOCK_MESSAGES: Message[] = SEEDS.map((s, i) => {
  const n = i + 1
  return {
    id: messageEntityId(n),
    request_id: requestEntityId(s.requestN),
    sender_id: s.senderId,
    body: s.body,
    attachments: [],
    read_at: s.read ? T(-s.minutesAgo + 30) : null,
    created_at: T(-s.minutesAgo),
  } satisfies Message
})

export function findMessagesByRequestId(requestId: string): Message[] {
  return MOCK_MESSAGES
    .filter((m) => m.request_id === requestId)
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
}

export function countUnreadMessagesForUser(userId: string): number {
  return MOCK_MESSAGES.filter((m) => m.sender_id !== userId && m.read_at === null).length
}
